import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useSendFeedback, type FeedbackType } from '@/api/feedback'

const TYPES: { value: FeedbackType; label: string; emoji: string }[] = [
  { value: 'bug', label: 'Bug', emoji: '🐛' },
  { value: 'idea', label: 'Idée', emoji: '💡' },
  { value: 'question', label: 'Question', emoji: '❓' },
]

const MIN_LENGTH = 3
const MAX_LENGTH = 2000

interface Props {
  open: boolean
  onClose: () => void
  /** Auto-attached context: the page/route the player is on. */
  page: string
  /** Captain pseudo, or null when logged out. */
  pseudo: string | null
}

/**
 * The "Un bug ? Une idée ? Une question ?" dialog: a type picker + a free-text
 * field. Context (page, pseudo) is attached automatically by the caller; the
 * server adds the browser. Best-effort delivery — a success toast is shown as
 * soon as the backend accepts the report.
 */
export function FeedbackModal({ open, onClose, page, pseudo }: Props) {
  const [type, setType] = useState<FeedbackType>('bug')
  const [message, setMessage] = useState('')
  const send = useSendFeedback()
  const dialogRef = useRef<HTMLDivElement>(null)

  // Reset to a clean slate every time the dialog (re)opens.
  useEffect(() => {
    if (open) {
      setType('bug')
      setMessage('')
    }
  }, [open])

  // Close on Escape.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Keep keyboard focus inside the dialog while open (aria-modal contract):
  // Tab past the last focusable wraps to the first, and vice-versa.
  const handleTabTrap = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !dialogRef.current) return
    const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
      'button:not([disabled]), textarea, input, [href], [tabindex]:not([tabindex="-1"])',
    )
    if (focusables.length === 0) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  if (!open) return null

  const trimmed = message.trim()
  const canSubmit = trimmed.length >= MIN_LENGTH && !send.isPending

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    send.mutate(
      { type, message: trimmed, page, pseudo: pseudo ?? undefined },
      {
        onSuccess: () => {
          toast.success('Merci ! Ton message a bien été transmis 🪶')
          onClose()
        },
        onError: () => {
          toast.error("Oups, l'envoi a échoué. Réessaie dans un instant.")
        },
      },
    )
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ocean-900/80 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-title"
        className="w-full sm:max-w-md bg-ocean-900 border border-sand-500/25 rounded-t-2xl sm:rounded-2xl shadow-2xl p-5"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleTabTrap}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <h2 id="feedback-title" className="font-display text-lg text-sand-300">
            Un bug ? Une idée ? Une question ?
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="shrink-0 grid place-items-center w-8 h-8 -mr-1 -mt-1 rounded-md text-ocean-300 hover:text-sand-200 hover:bg-ocean-700/40 transition-colors"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="flex gap-2 mb-3" role="group" aria-label="Type de retour">
            {TYPES.map((t) => {
              const selected = type === t.value
              return (
                <button
                  key={t.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setType(t.value)}
                  className={`flex-1 px-2 py-2 rounded-lg border text-sm transition-colors ${
                    selected
                      ? 'border-sand-500 bg-sand-500/15 text-sand-200'
                      : 'border-ocean-500/30 text-ocean-100 hover:border-sand-300/40 hover:text-sand-200'
                  }`}
                >
                  <span aria-hidden="true" className="mr-1">{t.emoji}</span>
                  {t.label}
                </button>
              )
            })}
          </div>

          <label htmlFor="feedback-message" className="sr-only">
            Ton message
          </label>
          <textarea
            id="feedback-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={MAX_LENGTH}
            rows={4}
            autoFocus
            placeholder="Décris ce que tu as vu, ton idée ou ta question…"
            className="w-full rounded-lg bg-ocean-900 border border-ocean-500/30 px-3 py-2 text-sm text-ocean-50 placeholder:text-ocean-300/60 focus:border-sand-300/50 focus:outline-none resize-y"
          />

          <div className="mt-1 flex items-center justify-between text-xs text-ocean-300">
            <span>
              {pseudo ? (
                <>Capitaine <strong className="text-sand-300">{pseudo}</strong></>
              ) : (
                'Envoi anonyme'
              )}
            </span>
            <span className="tabular-nums">{trimmed.length}/{MAX_LENGTH}</span>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 rounded-lg text-sm text-ocean-100 hover:text-sand-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-sand-500 text-ocean-900 hover:bg-sand-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {send.isPending ? 'Envoi…' : 'Envoyer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
